import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../../Components/Loading/Loading";
import Translate, {
  useTranslateText,
} from "../../Components/Multilanguage/Translate";
import { checkIfEmailExists } from "../Subscription/service";
import { login } from "../Accounts/Login/service";
import { registerUser } from "../Accounts/Register/service";
import { authenticate2, analytics2, authenticate } from "../../network/service";
import { getAccessToken } from "../../Redux/AToken/ATokenSlice";
import { getUser } from "../../Redux/UserSlice/UserSlice";
import { getUserDetails } from "../../Redux/UserDetailsSlice/UserDetailsSlice";
import { getLanguageModal } from "../../Redux/LanguageModal/languageModalSlice";
import { User } from "lucide-react";
import { redeemGiftCard } from "./service";

const GiftClaim = () => {
  const [authSectionExpanded, setAuthSectionExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState("create");
  const [values, setValues] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [giftCode, setGiftCode] = useState("");
  const [giftError, setGiftError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translateText = useTranslateText();

  const accessToken = useSelector((state) => state.accessToken?.value);
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const user = useSelector((state) => state?.user?.value);
  const userDetails = useSelector((state) => state?.userDetails?.value);
  const theme = projectInfo?.projectConfig?.config?.THEME; 
  useEffect(() => {
    if (user) {
      const urlParams = new URLSearchParams(window.location.search);
      const gc = urlParams.get("gc");
      if (gc) {
        setGiftCode(gc.toUpperCase());
      }
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const appInfo = {
    accessToken: accessToken,
    projectDetails: projectInfo,
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEmailValidation = async () => {
    if (authMode !== "create") {
      return false;
    } else {
      const isEmailValid = await validateEmail(values.email);
      if (isEmailValid) {
        setErrors({
          ["email"]: "",
          ["password"]: "",
        });
        values.email && isEmailAlreadyRegistered();
      } else {
        setErrors({
          ["email"]: <Translate textKey={"invalid_email_id"} />,
          ["password"]: "",
        });
      }
    }
  };

  const validateEmail = async (email) => {
    const regex = /^[^@]+@[^@]+\.[^@]+$/;
    return regex.test(email);
  };

  const isEmailAlreadyRegistered = async () => {
    const response = await checkIfEmailExists(appInfo, {
      user_email: values.email,
    });

    if (response?.status == 201) {
      setAuthMode("signin");
    } else {
      //   console.log(response?.data?.message)
    }
  };

  const validateForm = async () => {
    const isEmailValid = await validateEmail(values.email);
    const isPasswordValid = await validatePassword(values.password);

    if (isEmailValid && isPasswordValid) {
      setErrors({});
      return true;
    } else {
      manageErrorMessage(isEmailValid, isPasswordValid);
      return false;
    }
  };

  const validatePassword = async (password) => {
    if (password && password != "") {
      return true;
    } else {
      setErrors({
        ...errors,
        ["password"]: <Translate textKey={"password_required"} />,
      });
      return false;
    }
  };

  const manageErrorMessage = (isEmailValid, isPasswordValid) => {
    const passwordMsg = isPasswordValid ? (
      ""
    ) : (
      <Translate textKey={"password_required"} />
    );
    const emailMsg = isEmailValid ? (
      ""
    ) : (
      <Translate textKey={"email_required_message"} />
    );
    setErrors({
      password: passwordMsg,
      email: emailMsg,
    });
  };

  useEffect(() => {
    if (user) {
      setAuthSectionExpanded(false);
    }
  }, [user]);

  const updateDeviceAnalytics = async (
    userId,
    appInfo,
    userEmail,
    userName
  ) => {
    await analytics2(userId, appInfo, userEmail, userName);
  };

  const tokenAuthenticate = async () => {
    const response = await authenticate(appInfo);

    localStorage.setItem(
      "selectedLanguageCode",
      response?.data?.user_language?.short_code
        ? response?.data?.user_language?.short_code
        : response?.user_language?.short_code
    );
    localStorage.setItem(
      "selectedLanguageId",
      response?.data?.user_language?.language_id
        ? response?.data?.user_language?.language_id
        : response?.user_language?.language_id
    );

    dispatch(
      getLanguageModal({
        languageModal: {
          selectedLanguageCode: response?.data?.user_language?.short_code
            ? response?.data?.user_language?.short_code
            : response?.user_language?.short_code,
        },
      })
    );

    dispatch(
      getAccessToken({
        accessToken: response?.data?.token
          ? response?.data?.token
          : response?.token,
      })
    );

    dispatch(
      getUserDetails({
        userDetails: response?.data ? response?.data : response,
      })
    );
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    if (authMode === "create") {
      if (!(await validateForm())) return;
    } else {
      if (!(await validateForm())) return;
    }

    try {
      setLoading(true);

      let response;
      if (authMode === "create") {
        response = await registerUser(appInfo, {
          email: values.email,
          password: values.password,
        });
      } else {
        response = await login(
          { email: values.email, password: values.password },
          appInfo
        );
      }

      if (response.status === 200 && response.data) {
        localStorage.setItem("userId", response.data.data[0]?.user_id);
        tokenAuthenticate();
        updateDeviceAnalytics(
          response.data.data[0]?.user_id,
          appInfo,
          response.data.data[0]?.user_email,
          response.data.data[0]?.first_name
        );
        setTimeout(() => {
          dispatch(
            getUser({
              user: response.data.data[0]?.user_id,
            })
          );
        }, 1000);

        toast.success(
          authMode === "create" ? (
            <Translate textKey={"account_created_successfully"} />
          ) : (
            <Translate textKey={"login_successful"} />
          )
        );
        setAuthSectionExpanded(false);
      } else if (response?.status == 201) {
        toast.info(response?.data?.message);
      } else {
        toast.error(response.data?.message);
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimGift = async (e) => {
    e.preventDefault();

    if (!giftCode.trim()) {
      setGiftError(<Translate textKey={"please_enter_gift_code"} />);
      return;
    }

    // if (!user) {
    //   toast.error(<Translate textKey="please_login_to_continue" />);
    //   return;
    // }

    try {
      setLoading(true);
      setGiftError("");

      const response = await redeemGiftCard(appInfo, giftCode);

      if (response?.status === 200 && response?.data?.success) {
        toast.success(response.data.message);

        if (response.data.data) {
          const giftData = response.data.data;
        }

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setGiftError(<Translate textKey={"invalid_gift_code"} />);
      }
    } catch (error) {
      setGiftError(<Translate textKey={"invalid_gift_code"} />);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setGiftCode(value);
    if (giftError) setGiftError("");
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <ToastContainer />
      <div className="giftClaimPage">
        <div className={theme === 'LIGHT' ? 'claimContainer' : 'claimContainer Dark'}>
          <h1 className="pageTitle">
            <Translate textKey="claim_your_gift_card" />
          </h1>

          {!user && (
            <div className="auth-section">
              <div className="auth-mode-toggle">
                <span
                  className={authMode === "create" ? "active" : ""}
                  onClick={() => setAuthMode("create")}
                >
                  <Translate textKey="create_account" />
                </span>
                <span
                  className={authMode === "signin" ? "active" : ""}
                  onClick={() => setAuthMode("signin")}
                >
                  <Translate textKey="sign_in" />
                </span>
              </div>

              <form onSubmit={handleAuth} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email">
                    <Translate textKey="email" />
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={values.email}
                    onChange={handleInputChange}
                    onFocus={() => {
                      setErrors({ ...errors, ["email"]: "" });
                    }}
                    placeholder={translateText("email")}
                    className={errors.email ? "error" : ""}
                  />
                  {errors.email && (
                    <span className="error-text">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <Translate textKey="password" />
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={values.password}
                    onChange={handleInputChange}
                    onFocus={() => {
                      handleEmailValidation();
                      setErrors({ ...errors, ["password"]: "" });
                    }}
                    placeholder={translateText("password")}
                    className={errors.password ? "error" : ""}
                  />
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                </div>

                <button type="submit" className="continue-btn">
                  <Translate textKey="continue" />
                </button>

                <div className="auth-switch-text">
                  {authMode === "create" ? (
                    <>
                      <Translate textKey="register_sign_in_text" />{" "}
                      <span
                        onClick={() => setAuthMode("signin")}
                        className="switch-link"
                      >
                        <Translate textKey="sign_in" />
                      </span>
                    </>
                  ) : (
                    <>
                      <Translate textKey="sign_in_register_text" />{" "}
                      <span
                        onClick={() => setAuthMode("create")}
                        className="switch-link"
                      >
                        <Translate textKey="sign_up" />
                      </span>
                    </>
                  )}
                </div>
              </form>
            </div>
          )}

          <div className="claim-section">
            <form onSubmit={handleClaimGift} className="claim-form">
              <div className="form-group">
                <label htmlFor="gift-code">
                  <Translate textKey="code" />
                </label>
                <input
                  type="text"
                  id="gift-code"
                  value={giftCode}
                  onChange={handleCodeChange}
                  placeholder={translateText("enter_your_gift_code")}
                  className={giftError ? "error" : ""}
                  maxLength={20}
                  disabled={!user}
                />
                {giftError && <span className="error-text">{giftError}</span>}
                {!user && (
                  <span className="info-text">
                    <Translate textKey="gift_login_message" />
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="claim-btn"
                disabled={!giftCode.trim() || !user}
              >
                <Translate textKey="claim" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftClaim;
